import {createSelector, type Selector} from 'reselect';

import {type Ref as PanelRef} from '../../../../../state/views/panel/types';
import {makeSectionMediaSettingsSelector} from '../../../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../../../state/views/panelBankSectionConfig/types';
import {makeMaybeWorkspaceSettingsMediaSelector} from '../../../../../state/views/workspaceSettings/selectors';
import {type Ref as WorkspaceSettingsRef} from '../../../../../state/views/workspaceSettings/types';
import {type RootState} from '../../../../../types/redux';
import {panelViews} from '../../../../PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../../PanelMediaBrowser/common';
import {resolveNullableCascade} from '../../getCascadingSetting';
import {SettingsLevel} from '../types';

/** Read a panel part from the normalized view state. */
const selectPanel = (panelRef: PanelRef) => (state: RootState) =>
  state.views.parts[panelRef.type][panelRef.id];

/**
 * How a value cascades across the workspace → section → panel hierarchy.
 *
 * - `child-wins`: the most specific level that has a value wins
 *   (`panel ?? section ?? workspace ?? default`). This matches the
 *   `getCascadedMediaSettings` spread semantics and is correct for the vast
 *   majority of media settings.
 * - `parent-wins`: the least specific level that has a value wins
 *   (`workspace ?? section ?? panel ?? default`). Only used for
 *   `videoPlaybackSync`, where enabling sync at a higher level locks the
 *   lower levels on.
 * - `nullable-child-wins`: like `child-wins`, but a stored `null` means
 *   "explicitly cleared — stop inheriting and use the default" (matching the
 *   `resolveNullableCascade` convention used for axis range bounds). Use this
 *   for fields where the default state is a meaningful choice a child level
 *   must be able to make when a parent has set a value (e.g. "Show all" for
 *   `maxGalleryItems`). `undefined` still means "not set — inherit."
 */
export type CascadeRule = 'child-wins' | 'parent-wins' | 'nullable-child-wins';

/**
 * Custom cascade resolution for settings whose semantics aren't captured by
 * the built-in rules. Receives the raw values ordered most-specific to
 * least-specific (panel, section, workspace); a `undefined` result falls back
 * to the setting's default.
 */
export type CascadeResolver<K extends keyof MediaBrowserPanelConfig> = (
  childToParent: Array<FieldValue<K>>
) => FieldValue<K>;

/**
 * Extra caller-supplied arguments a setting's selectors need beyond the refs
 * (e.g. `isSingleRun` for gallery/grid defaults). `void` means none: the
 * generated selector factories then take no trailing params argument.
 */
export type ParamsArgs<P> = [P] extends [void] ? [] : [params: P];

export const ALL_SETTINGS_LEVELS: SettingsLevel[] = [
  SettingsLevel.WORKSPACE,
  SettingsLevel.SECTION,
  SettingsLevel.PANEL,
];

/**
 * Declarative description of a single media setting. This is the only per-field
 * input required to generate the full selector family below.
 */
export type MediaSettingSpec<
  K extends keyof MediaBrowserPanelConfig,
  P = void,
> = {
  field: K;
  /**
   * Value used when no level has set the field. When omitted, the cascaded /
   * display selectors resolve to `undefined` if no level has a value.
   */
  defaultValue?: MediaBrowserPanelConfig[K];
  /** Defaults to `child-wins`. */
  cascade?: CascadeRule | CascadeResolver<K>;
  /**
   * The levels at which this setting can be set. Defaults to all three.
   * Selectors for absent levels always resolve to `undefined`, so those
   * levels never contribute to the cascade. This is the source of truth the
   * drawer UI (`util.ts`) derives its per-setting levels from.
   */
  levels?: SettingsLevel[];
  /**
   * Default derived from caller-supplied params instead of a static value
   * (e.g. gallery/grid defaults depend on `isSingleRun`). Applied everywhere
   * `defaultValue` would be: in the cascaded and display selectors.
   */
  getDefault?: (params: P) => ResolvedValue<K>;
  /**
   * Default derived from other Redux state, applied only as the display
   * selector's final fallback (e.g. columnCount's default depends on the
   * effective `mode`). The cascaded selectors stay presence-based and may
   * resolve to `undefined`.
   */
  makeDisplayDefaultSelector?: (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    settingsLevel: SettingsLevel,
    panelRef: PanelRef | undefined,
    ...params: ParamsArgs<P>
  ) => Selector<RootState, ResolvedValue<K>>;
};

export type FieldValue<K extends keyof MediaBrowserPanelConfig> =
  MediaBrowserPanelConfig[K];
export type ResolvedValue<K extends keyof MediaBrowserPanelConfig> =
  NonNullable<MediaBrowserPanelConfig[K]>;

/**
 * The complete set of selector factories for one media setting. Instances are
 * created once per field at module load, so the returned factories are stable
 * references safe to pass to `useMemoizedSelector`.
 *
 * `R` is the type produced by the cascaded / display selectors: `ResolvedValue`
 * when the spec has a `defaultValue`, otherwise `FieldValue` (may be
 * undefined).
 */
export type MediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
  R = ResolvedValue<K>,
  D = R,
  P = void,
> = {
  /** Raw workspace-level value from the view spec (undefined if unset). */
  makeWorkspaceSelector: (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined
  ) => Selector<RootState, FieldValue<K>>;
  /** Raw section-level value from the view spec (not cascaded). */
  makeSectionSelector: (
    sectionRef: SectionRef | undefined
  ) => Selector<RootState, FieldValue<K>>;
  /** Raw panel-level value from the panel's own config (not cascaded). */
  makePanelSelector: (
    panelRef: PanelRef | undefined
  ) => Selector<RootState, FieldValue<K>>;
  /** Workspace → section cascaded value. */
  makeCascadingSectionSelector: (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    ...params: ParamsArgs<P>
  ) => Selector<RootState, R>;
  /** Workspace → section → panel cascaded value. */
  makeCascadingPanelSelector: (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    panelRef: PanelRef | undefined,
    ...params: ParamsArgs<P>
  ) => Selector<RootState, R>;
  /** Effective value to display at a given settings level. */
  makeDisplaySelector: (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    settingsLevel: SettingsLevel,
    panelRef?: PanelRef,
    ...params: ParamsArgs<P>
  ) => Selector<RootState, D>;
};

/** Read a single field off a media panel's config, if it is a media panel. */
function makePanelFieldSelector<K extends keyof MediaBrowserPanelConfig>(
  field: K,
  panelRef: PanelRef | undefined
): Selector<RootState, FieldValue<K>> {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig)[field]
      : undefined
  );
}

function getDefaultValue<K extends keyof MediaBrowserPanelConfig, P>(
  spec: MediaSettingSpec<K, P>,
  params: ParamsArgs<P>
): FieldValue<K> {
  return spec.getDefault != null
    ? spec.getDefault(params[0] as P)
    : spec.defaultValue;
}

/**
 * Resolve a cascade given values ordered from most-specific to least-specific
 * (panel, section, workspace). `parent-wins` simply reverses the precedence;
 * a custom resolver function takes full control of the precedence, with an
 * `undefined` result still falling back to the default.
 */
function resolveCascade<K extends keyof MediaBrowserPanelConfig, P>(
  spec: MediaSettingSpec<K, P>,
  childToParent: Array<FieldValue<K>>,
  params: ParamsArgs<P>
): FieldValue<K> {
  let resolved: FieldValue<K>;
  if (typeof spec.cascade === 'function') {
    resolved = spec.cascade(childToParent);
  } else if (spec.cascade === 'nullable-child-wins') {
    // resolveNullableCascade expects lowest-to-highest precedence.
    resolved = resolveNullableCascade(...[...childToParent].reverse());
  } else {
    const ordered =
      spec.cascade === 'parent-wins'
        ? [...childToParent].reverse()
        : childToParent;
    resolved = ordered.find(value => value != null);
  }
  return resolved ?? getDefaultValue(spec, params);
}

/**
 * Generate the full selector family for one media setting from its spec.
 *
 * The generated selectors are behavior-identical to the hand-written ones they
 * replace: same `??`-based cascade (child-wins), same workspace/section slice
 * reads, same `SettingsLevel` switch in the display selector.
 */
export function makeMediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
>(
  spec: MediaSettingSpec<K> & {
    defaultValue: NonNullable<MediaBrowserPanelConfig[K]>;
  }
): MediaSettingSelectorFamily<K, ResolvedValue<K>>;
export function makeMediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
  P,
>(
  spec: MediaSettingSpec<K, P> & {
    getDefault: (params: P) => ResolvedValue<K>;
  }
): MediaSettingSelectorFamily<K, ResolvedValue<K>, ResolvedValue<K>, P>;
export function makeMediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
  P = void,
>(
  spec: MediaSettingSpec<K, P> & {
    makeDisplayDefaultSelector: NonNullable<
      MediaSettingSpec<K, P>['makeDisplayDefaultSelector']
    >;
  }
): MediaSettingSelectorFamily<K, FieldValue<K>, ResolvedValue<K>, P>;
export function makeMediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
  P = void,
>(
  spec: MediaSettingSpec<K, P>
): MediaSettingSelectorFamily<K, FieldValue<K>, FieldValue<K>, P>;
export function makeMediaSettingSelectorFamily<
  K extends keyof MediaBrowserPanelConfig,
  P = void,
>(
  spec: MediaSettingSpec<K, P>
): MediaSettingSelectorFamily<K, FieldValue<K>, FieldValue<K>, P> {
  const {field, levels = ALL_SETTINGS_LEVELS} = spec;
  const hasWorkspaceLevel = levels.includes(SettingsLevel.WORKSPACE);
  const hasSectionLevel = levels.includes(SettingsLevel.SECTION);
  const hasPanelLevel = levels.includes(SettingsLevel.PANEL);

  const makeWorkspaceSelector = (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined
  ): Selector<RootState, FieldValue<K>> => {
    if (workspaceSettingsRef == null || !hasWorkspaceLevel) {
      return () => undefined;
    }
    return createSelector(
      makeMaybeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
      media => media?.[field]
    );
  };

  const makeSectionSelector = (
    sectionRef: SectionRef | undefined
  ): Selector<RootState, FieldValue<K>> => {
    if (!hasSectionLevel) {
      return () => undefined;
    }
    return createSelector(
      makeSectionMediaSettingsSelector(sectionRef),
      media => media?.[field]
    );
  };

  const makePanelSelector = (
    panelRef: PanelRef | undefined
  ): Selector<RootState, FieldValue<K>> =>
    makePanelFieldSelector(field, hasPanelLevel ? panelRef : undefined);

  const makeCascadingSectionSelector = (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    ...params: ParamsArgs<P>
  ): Selector<RootState, FieldValue<K>> =>
    createSelector(
      makeSectionSelector(sectionRef),
      makeWorkspaceSelector(workspaceSettingsRef),
      (sectionValue, workspaceValue) =>
        resolveCascade(spec, [sectionValue, workspaceValue], params)
    );

  const makeCascadingPanelSelector = (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    panelRef: PanelRef | undefined,
    ...params: ParamsArgs<P>
  ): Selector<RootState, FieldValue<K>> =>
    createSelector(
      makePanelSelector(panelRef),
      makeSectionSelector(sectionRef),
      makeWorkspaceSelector(workspaceSettingsRef),
      (panelValue, sectionValue, workspaceValue) =>
        resolveCascade(spec, [panelValue, sectionValue, workspaceValue], params)
    );

  const makeDisplaySelector = (
    workspaceSettingsRef: WorkspaceSettingsRef | undefined,
    sectionRef: SectionRef | undefined,
    settingsLevel: SettingsLevel,
    panelRef?: PanelRef,
    ...params: ParamsArgs<P>
  ): Selector<RootState, FieldValue<K>> => {
    const baseSelector = createSelector(
      makeWorkspaceSelector(workspaceSettingsRef),
      makeCascadingSectionSelector(workspaceSettingsRef, sectionRef, ...params),
      makeCascadingPanelSelector(
        workspaceSettingsRef,
        sectionRef,
        panelRef,
        ...params
      ),
      (workspaceValue, sectionCascading, panelCascading) => {
        switch (settingsLevel) {
          case SettingsLevel.PANEL:
            return panelCascading;
          case SettingsLevel.SECTION:
            return sectionCascading;
          default:
            return workspaceValue ?? getDefaultValue(spec, params);
        }
      }
    );
    if (spec.makeDisplayDefaultSelector == null) {
      return baseSelector;
    }
    return createSelector(
      baseSelector,
      spec.makeDisplayDefaultSelector(
        workspaceSettingsRef,
        sectionRef,
        settingsLevel,
        panelRef,
        ...params
      ),
      (value, displayDefault) => value ?? displayDefault
    );
  };

  return {
    makeWorkspaceSelector,
    makeSectionSelector,
    makePanelSelector,
    makeCascadingSectionSelector,
    makeCascadingPanelSelector,
    makeDisplaySelector,
  };
}
